import { type AppearanceMode, type ResolvedTheme } from '@/hooks/appearance-mode.types';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { setAppearanceUserProperties } from '@/providers/amplitude-user-properties';
import { Theme } from '@heidi/schema/ml-scribe';
import { useTheme } from 'next-themes';
import { useCallback, useMemo } from 'react';

// Re-exported so existing consumers (`AppearanceModeMenuItems`, etc.) keep a
// single discoverable entry point for the type. The canonical declarations
// live in `src/types/appearance-mode.types.ts`.
export type { AppearanceMode, ResolvedTheme };

export const APPEARANCE_MODE_KEY = 'appearance-mode';

const THEME_TO_MODE: Record<string, AppearanceMode> = {
  [Theme.newBrand]: 'light',
  [Theme.newBrandDark]: 'dark',
};

export const MODE_TO_THEME: Record<Exclude<AppearanceMode, 'system'>, Theme> = {
  light: Theme.newBrand,
  dark: Theme.newBrandDark,
};

interface SetAppearanceModeOptions {
  suppressNetworkSideEffects?: boolean;
}

export function getStoredAppearanceMode(): AppearanceMode | null {
  try {
    const stored = localStorage.getItem(APPEARANCE_MODE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
  } catch {
    // SSR or storage unavailable
  }
  return null;
}

export function persistAppearanceMode(mode: AppearanceMode): void {
  try {
    localStorage.setItem(APPEARANCE_MODE_KEY, mode);
  } catch {
    // Storage unavailable
  }
}

/**
 * Resolve `mode` to the theme the user actually sees, synchronously.
 * For `system` we read `window.matchMedia` directly rather than relying on
 * `next-themes`'s `resolvedTheme`, which lags by one render cycle when a
 * mode change is dispatched in the same tick.
 */
export function computeResolvedTheme(mode: AppearanceMode): ResolvedTheme {
  if (mode === 'dark') return 'dark';
  if (mode === 'light') return 'light';
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Manages appearance mode (Light / Dark / System) using newBrand and newBrandDark themes.
 *
 * The chosen mode is persisted to localStorage for next-themes and UserThemeManager.
 * The backend user.theme field is intentionally not synced here — importing
 * useMutateUpdateUser pulls in scribeAPI → authModel → store → authModel (circular),
 * and the async query cache caused a flicker race condition with UserThemeManager.
 * If cross-device sync is needed later, it should be done outside the React render path.
 */
export function useAppearanceMode() {
  const { theme, setTheme, resolvedTheme } = useTheme();

  const mode: AppearanceMode = useMemo(() => {
    if (theme === 'system') return 'system';
    return THEME_TO_MODE[theme ?? ''] ?? 'light';
  }, [theme]);

  const setMode = useCallback(
    (newMode: AppearanceMode, options: SetAppearanceModeOptions = {}) => {
      const previousMode = mode;
      const isFirstChangeOnDevice = getStoredAppearanceMode() === null;
      const resolvedAfter = computeResolvedTheme(newMode);
      const suppressNetworkSideEffects = options.suppressNetworkSideEffects === true;

      persistAppearanceMode(newMode);

      if (newMode === 'system') {
        setTheme('system');
      } else {
        setTheme(MODE_TO_THEME[newMode]);
      }

      if (!suppressNetworkSideEffects && newMode !== previousMode) {
        // Intentionally sent to both Snowplow + Amplitude (no `amplitude: false`).
        // The new-event policy in `.ai-context/analytics.md` prefers Snowplow-only,
        // but the practical analytics surface (Amplitude `Identify` user-property
        // cohorting + existing event dashboards) lives in Amplitude — see
        // docs/design-docs/dark-mode-flicker-fix.md §7.5.
        track({
          event_name: TrackingEventName.AppearanceModeChanged,
          props: {
            mode: newMode,
            previous_mode: previousMode,
            resolved_theme: resolvedAfter,
            entry_point: 'user_menu',
            is_first_change_on_device: isFirstChangeOnDevice,
          },
        });
      }

      // Unconditional (vs. the gated event above): re-selecting the same
      // `mode` can still change `resolvedAfter` (e.g. picking `system` after
      // the OS preference flipped), and the helper's tuple-dedup cache makes
      // the call free when nothing actually changed.
      if (!suppressNetworkSideEffects) {
        setAppearanceUserProperties(newMode, resolvedAfter);
      }
    },
    [setTheme, mode]
  );

  const isDarkResolved = resolvedTheme === Theme.newBrandDark || resolvedTheme === 'dark';

  return { mode, setMode, resolvedTheme, isDarkResolved } as const;
}
